import chromaJs from 'chroma-js';

// src/waveform.ts
var DEFAULT_OPTIONS = {
  sampleCount: 1e3,
  channel: -1,
  // Average both channels
  normalize: true
};
function analyzeWaveform(audioBuffer, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { sampleCount, channel, normalize } = opts;
  let channelData;
  if (channel === -1 && audioBuffer.numberOfChannels > 1) {
    const left = audioBuffer.getChannelData(0);
    const right = audioBuffer.getChannelData(1);
    channelData = new Float32Array(left.length);
    for (let i = 0; i < left.length; i++) {
      channelData[i] = (left[i] + right[i]) / 2;
    }
  } else {
    channelData = audioBuffer.getChannelData(Math.max(0, channel));
  }
  const amplitudes = downsample(channelData, sampleCount);
  let peak = 0;
  let sumSquares = 0;
  for (const amplitude of amplitudes) {
    const abs = Math.abs(amplitude);
    if (abs > peak) peak = abs;
    sumSquares += amplitude * amplitude;
  }
  const rms = Math.sqrt(sumSquares / amplitudes.length);
  const finalAmplitudes = normalize ? normalizeAmplitude(amplitudes) : amplitudes;
  return {
    amplitudes: finalAmplitudes,
    peak,
    rms,
    duration: audioBuffer.duration,
    sampleRate: audioBuffer.sampleRate
  };
}
function downsample(data, targetSamples) {
  const result = [];
  const blockSize = Math.floor(data.length / targetSamples);
  for (let i = 0; i < targetSamples; i++) {
    const start = i * blockSize;
    const end = Math.min(start + blockSize, data.length);
    for (let j = start; j < end; j++) {
      Math.abs(data[j]);
    }
    result.push(data[start + Math.floor(blockSize / 2)] || 0);
  }
  return result;
}
function normalizeAmplitude(data) {
  const array = Array.isArray(data) ? data : Array.from(data);
  const peak = Math.max(...array.map(Math.abs));
  if (peak === 0) return array.map(() => 0);
  return array.map((value) => value / peak);
}
function extractFrequencyData(_audioContext, audioBuffer, fftSize = 2048) {
  const offlineContext = new OfflineAudioContext(
    audioBuffer.numberOfChannels,
    audioBuffer.length,
    audioBuffer.sampleRate
  );
  const source = offlineContext.createBufferSource();
  const analyser = offlineContext.createAnalyser();
  analyser.fftSize = fftSize;
  source.buffer = audioBuffer;
  source.connect(analyser);
  analyser.connect(offlineContext.destination);
  const frequencyData = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteFrequencyData(frequencyData);
  return frequencyData;
}
var DEFAULT_OPTIONS2 = {
  baseHue: Math.random() * 360,
  chroma: 80,
  lightness: 60,
  strategy: "complementary"
};
function generateComplementaryPalette(count, options = {}) {
  const opts = { ...DEFAULT_OPTIONS2, ...options };
  const { baseHue, chroma: chromaVal, lightness, strategy } = opts;
  const hues = getHuesForStrategy(baseHue, count, strategy);
  return hues.map((hue) => chromaJs.lch(lightness, chromaVal, hue).hex());
}
function generateRandomPalette(count) {
  const baseHue = Math.random() * 360;
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = (baseHue + i * 360 / count) % 360;
    const l = 40 + Math.random() * 40;
    const c = 60 + Math.random() * 40;
    colors.push(chromaJs.lch(l, c, hue).hex());
  }
  return colors;
}
function generateGradient(color1, color2, steps) {
  return chromaJs.scale([color1, color2]).mode("lch").colors(steps);
}
function getHuesForStrategy(baseHue, count, strategy) {
  switch (strategy) {
    case "complementary":
      return Array.from({ length: count }, (_, i) => (baseHue + i * 360 / count) % 360);
    case "analogous":
      return Array.from({ length: count }, (_, i) => (baseHue + i * 60 / count - 30) % 360);
    case "triadic":
      return Array.from({ length: Math.min(count, 3) }, (_, i) => (baseHue + i * 120) % 360);
    case "split-complementary":
      if (count === 1) return [baseHue];
      if (count === 2) return [baseHue, (baseHue + 180) % 360];
      return [baseHue, (baseHue + 150) % 360, (baseHue + 210) % 360];
    default:
      return Array.from({ length: count }, (_, i) => (baseHue + i * 360 / count) % 360);
  }
}
function isValidColor(color) {
  try {
    chromaJs(color);
    return true;
  } catch {
    return false;
  }
}
function hexToLch(hex) {
  const [l, c, h] = chromaJs(hex).lch();
  return { l, c, h: h || 0 };
}

export { analyzeWaveform, extractFrequencyData, generateComplementaryPalette, generateGradient, generateRandomPalette, hexToLch, isValidColor, normalizeAmplitude };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map